import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectGiveawayData } from '@/shared';
import { GetGiveawayDataResponseType } from '@/entities';

import { Task } from '../task-item';

export type CriteriaKey = 'vote' | 'quiz';

export interface CriteriaTask extends Task {
  criteriaKey: CriteriaKey;
}

const CRITERIA_KEY_MAP: Record<
  CriteriaKey,
  keyof NonNullable<GetGiveawayDataResponseType['meets_criteria']>
> = {
  vote: 'voted',
  quiz: 'completed_tasks'
};

const resolveTaskCompletion = (
  task: CriteriaTask,
  meetsCriteria: GetGiveawayDataResponseType['meets_criteria'] | undefined
): boolean => {
  const apiKey = CRITERIA_KEY_MAP[task.criteriaKey];
  return apiKey ? Boolean(meetsCriteria?.[apiKey]) : task.completed;
};

export const useUnlockGift = (tasks?: Task[]) => {
  const giveawayData = useSelector(selectGiveawayData);
  const meetsCriteria = giveawayData?.meets_criteria;

  const resolvedTasks = useMemo(() => {
    const baseTasks = (tasks as CriteriaTask[] | undefined) ?? [];

    return baseTasks.map((task) => ({
      ...task,
      completed: resolveTaskCompletion(task, meetsCriteria)
    }));
  }, [tasks, meetsCriteria]);

  const completedCount = useMemo(() => {
    return resolvedTasks.filter((task) => task.completed).length;
  }, [resolvedTasks]);

  const totalCount = resolvedTasks.length;
  const isAllCompleted = completedCount === totalCount;

  return {
    resolvedTasks,
    completedCount,
    totalCount,
    isAllCompleted
  };
};
